import { Add } from "@mui/icons-material";
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { BASE_URL } from "lib/utilities/globalConstants";
import { UserAuth } from "provider/AuthProvider";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";

const CreateCandidatesView = ({ formId, selectedCandidates = [], onViewCreated = () => {} }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [fetchingAdmins, setFetchingAdmins] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    otherAdminsId: [],
  });

  const { user } = UserAuth();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleAdminSelect = (event) => {
    const { value } = event.target;
    setFormData((prev) => ({
      ...prev,
      otherAdminsId: value,
    }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = "Name is required";
    }
    return errors;
  };

  const handleSubmit = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      setIsLoading(true);
      const response = await fetch(`${BASE_URL}admin/createCandidatesView`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${user.accessToken}`,
        },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          cfmIds: selectedCandidates,
          formId: formId,
          otherAdminsId: formData.otherAdminsId,
        }),
      });

      if (response.ok) {
        setOpenModal(false);

        setFormData({
          name: "",
          description: "",
          otherAdminsId: [],
        });
        toast.success("View created successfully");
        onViewCreated();
      } else {
        const errorData = await response.json();
        toast.error(errorData.message);
      }
    } catch (error) {
      console.error("Error creating view:", error);
      toast.error("An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchAdminData = async () => {
      try {
        setFetchingAdmins(true);
        const response = await fetch(`${BASE_URL}admin/getAllAdminData`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${user.accessToken}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setAdmins(data.admins || []);
        } else {
          const errorData = await response.json();
          toast.error(errorData.message);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setFetchingAdmins(false);
      }
    };

    if (openModal) {
      fetchAdminData();
    }
  }, [openModal, user.accessToken]);

  const filteredAdmins = admins.filter((admin) => admin.admin_id !== user.uid);

  return (
    <>
      <button
        type="button"
        disabled={selectedCandidates.length === 0}
        onClick={() => setOpenModal(true)}
        className="flex items-center justify-center px-3 py-1 rounded-md text-green-500 bg-green-100 hover:bg-green-200 transition-colors duration-200 border border-green-500 disabled:cursor-not-allowed disabled:opacity-70 text-sm"
      >
        <span className="mr-2">Create View</span>
        <Add style={{ height: "16px", width: "16px" }} />
      </button>
      <Dialog
        maxWidth="md"
        open={openModal}
        fullWidth
        onClose={() => !isLoading && setOpenModal(false)}
      >
        <div className="overflow-hidden flex flex-col">
          <div className="flex items-center justify-between border-b py-2 px-5 sticky top-0 bg-white z-50">
            <Typography variant="h5" className="font-semibold text-gray-900">
              Create Candidates View
            </Typography>
            <div className="flex justify-end gap-4">
              <button
                type="button"
                disabled={isLoading}
                onClick={() => setOpenModal(false)}
                className="text-blue-700 text-sm font-medium bg-blue-50 border border-blue-200 hover:bg-blue-100 transition-colors px-4 py-1.5 rounded-lg disabled:opacity-70 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isLoading || selectedCandidates.length === 0}
                className="text-white text-sm font-medium bg-blue-600 hover:bg-blue-700 transition-colors px-4 py-1.5 rounded-lg flex items-center disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isLoading ? <CircularProgress size={16} color="inherit" className="mr-2" /> : null}
                Create View
              </button>
            </div>
          </div>
          <div className="flex flex-col overflow-auto p-5 space-y-3">
            <div className="space-y-3 w-full">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-700">Title</p>
              </div>
              <FormControl error={!!formErrors.name} disabled={isLoading} fullWidth>
                <TextField
                  placeholder="Enter view name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  error={!!formErrors.name}
                  disabled={isLoading}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "#fff",
                    },
                  }}
                />
                <FormHelperText>{formErrors.name}</FormHelperText>
              </FormControl>
            </div>

            <div className="space-y-3 w-full">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-700">Description</p>
              </div>
              <FormControl fullWidth={true} disabled={isLoading}>
                <TextField
                  placeholder="Enter view description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  variant="outlined"
                  disabled={isLoading}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      background: "#fff",
                    },
                  }}
                />
              </FormControl>
            </div>

            <div className="space-y-3 w-full">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-gray-700">Share with other people</p>
              </div>
              <FormControl fullWidth disabled={isLoading || fetchingAdmins}>
                <Select
                  multiple
                  variant="outlined"
                  value={formData.otherAdminsId}
                  onChange={handleAdminSelect}
                  displayEmpty
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return <span className="text-neutral-400">Select others</span>;
                    }
                    return (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 0.5,
                          padding: "4px 0",
                        }}
                      >
                        {selected.map((adminId) => {
                          const admin = admins.find((a) => a.admin_id === adminId);
                          return <Chip key={adminId} label={admin?.user_name || adminId} />;
                        })}
                      </Box>
                    );
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 170,
                        marginTop: "8px",
                        borderRadius: "12px",
                      },
                    },
                  }}
                  sx={{
                    borderRadius: "12px",
                    background: "#fff",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderRadius: "12px",
                    },
                    "&.MuiOutlinedInput-root": {
                      borderRadius: "12px",
                    },
                    "& .MuiSelect-select": {
                      minHeight: "40px !important",
                      display: "flex",
                      alignItems: "center",
                      padding: "4px 14px !important",
                    },
                  }}
                >
                  {fetchingAdmins ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} />
                      <span className="ml-2">Loading admins...</span>
                    </MenuItem>
                  ) : filteredAdmins.length === 0 ? (
                    <MenuItem disabled>No other admins available</MenuItem>
                  ) : (
                    filteredAdmins.map((admin) => (
                      <MenuItem
                        key={admin.admin_id}
                        value={admin.admin_id}
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          mb: 0.5,
                        }}
                      >
                        <div>{admin.user_name || admin.user_email || admin.admin_id}</div>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          sx={{
                            fontSize: "0.75rem",
                          }}
                        >
                          {admin.role_name}
                        </Typography>
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </div>

            {/* Selected Candidates Info */}
            <div className="bg-blue-50 p-4 rounded-lg">
              <Typography variant="subtitle2" className="font-medium text-gray-800 mb-1">
                Selected Candidates: {selectedCandidates.length}
              </Typography>
              <Typography variant="body2" className="text-gray-600">
                You are creating a view with {selectedCandidates.length} selected candidate
                {selectedCandidates.length !== 1 ? "s" : ""}
              </Typography>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default CreateCandidatesView;
